/**
 * Demo Accounting Data
 *
 * Pre-active customers get demo data across the dashboard (see
 * `home/utils/demoData.ts`). Accounting keeps its generators here rather than
 * in that file for one concrete reason: **every amount below is in cents**,
 * matching the accounting API, while every amount in `demoData.ts` is in
 * dollars. Mixing the two units in one module is a footgun. `isPreActiveCustomer`
 * still comes from there — there's one definition of "pre-active".
 *
 * Everything is derived from two tables — payers and vendors. The income
 * statement is the sum of them, not a separate set of hand-written figures, so
 * the cards always tie out: top payers add up to gross revenue, vendor
 * categories add up to the expense breakdown, and the report pack for a year
 * matches the income statement for that year.
 */

import dayjs from 'dayjs';
import { REPORT_PACK_STATUS } from '../../../../../../constants/accounting';

// Baseline figures describe the current year. Earlier years are scaled back:
// revenue grows faster than costs, so the year-over-year comparison shows
// improving margins across the recent years a customer actually looks at. The
// two rates cross over about eight years back — far outside the range the hub
// offers by default, but it is not a claim that holds at every offset.
const REVENUE_GROWTH_PER_YEAR = 1.124;
const EXPENSE_GROWTH_PER_YEAR = 1.071;

type DemoPayer = {
  payer: string;
  total: number;
  transaction_count: number;
};

type DemoVendor = {
  vendor: string;
  category: string;
  total: number;
  transaction_count: number;
};

// Brokers and shippers an owner-operator would actually haul for.
const DEMO_PAYERS: DemoPayer[] = [
  { payer: 'C.H. Robinson', total: 6842000, transaction_count: 42 },
  { payer: 'TQL Logistics', total: 5218000, transaction_count: 31 },
  { payer: 'Landstar', total: 4476000, transaction_count: 27 },
  { payer: 'Coyote Logistics', total: 3890000, transaction_count: 24 },
  { payer: 'J.B. Hunt', total: 3145000, transaction_count: 19 },
  { payer: 'Uber Freight', total: 2610000, transaction_count: 17 },
  { payer: 'Echo Global Logistics', total: 1802000, transaction_count: 12 },
  { payer: 'RXO', total: 1475000, transaction_count: 9 },
];

// Categories match `EXPENSE_CATEGORIES`, so the breakdown pie and the category
// filter on Top Expenses both behave like they would with real data.
const DEMO_VENDORS: DemoVendor[] = [
  {
    vendor: 'Pilot Flying J',
    category: 'fuel',
    total: 3120000,
    transaction_count: 118,
  },
  {
    vendor: "Love's Travel Stop",
    category: 'fuel',
    total: 2464000,
    transaction_count: 96,
  },
  {
    vendor: 'TA Petro',
    category: 'fuel',
    total: 1580000,
    transaction_count: 61,
  },
  {
    vendor: 'Speedway',
    category: 'fuel',
    total: 902000,
    transaction_count: 47,
  },
  {
    vendor: 'Road Ranger',
    category: 'fuel',
    total: 576000,
    transaction_count: 33,
  },
  {
    vendor: 'Daimler Truck Financial',
    category: 'truck_payments',
    total: 4260000,
    transaction_count: 12,
  },
  {
    vendor: 'Progressive Commercial',
    category: 'insurance',
    total: 2880000,
    transaction_count: 12,
  },
  {
    vendor: 'TA Truck Service',
    category: 'preventative_maintenance',
    total: 812000,
    transaction_count: 9,
  },
  {
    vendor: 'Speedco',
    category: 'preventative_maintenance',
    total: 613000,
    transaction_count: 11,
  },
  {
    vendor: 'Rush Truck Centers',
    category: 'repairs',
    total: 742000,
    transaction_count: 5,
  },
  {
    vendor: 'Kenworth of Dallas',
    category: 'repairs',
    total: 456000,
    transaction_count: 3,
  },
  {
    vendor: 'Southern Tire Mart',
    category: 'tires',
    total: 612000,
    transaction_count: 8,
  },
  {
    vendor: 'Tire Shop',
    category: 'tires',
    total: 322000,
    transaction_count: 6,
  },
  {
    vendor: 'PrePass',
    category: 'tolls',
    total: 398500,
    transaction_count: 62,
  },
  {
    vendor: 'E-ZPass',
    category: 'tolls',
    total: 273000,
    transaction_count: 44,
  },
  {
    vendor: 'FMCSA Registration',
    category: 'licensing',
    total: 186000,
    transaction_count: 3,
  },
  {
    vendor: 'State DMV Renewal',
    category: 'licensing',
    total: 126000,
    transaction_count: 4,
  },
];

/** Round a cent amount to a whole dollar so scaled figures stay readable. */
const toWholeDollars = (cents: number): number => Math.round(cents / 100) * 100;

const yearsFromNow = (year: number): number => year - dayjs().year();

const scaledPayers = (year: number): DemoPayer[] => {
  const scale = REVENUE_GROWTH_PER_YEAR ** yearsFromNow(year);
  return DEMO_PAYERS.map((p) => ({
    ...p,
    total: toWholeDollars(p.total * scale),
    transaction_count: Math.round(p.transaction_count * scale),
  }));
};

const scaledVendors = (year: number): DemoVendor[] => {
  const scale = EXPENSE_GROWTH_PER_YEAR ** yearsFromNow(year);
  return DEMO_VENDORS.map((v) => ({
    ...v,
    total: toWholeDollars(v.total * scale),
    transaction_count: Math.round(v.transaction_count * scale),
  }));
};

const sumBy = <T>(items: T[], pick: (item: T) => number): number =>
  items.reduce((total, item) => total + pick(item), 0);

type DemoTotals = {
  gross_revenue: number;
  total_expenses: number;
  net_profit: number;
  by_category: Record<string, number>;
  uncategorized_count: number;
  uncategorized_total: number;
  transaction_count: number;
};

const totalsForYear = (year: number): DemoTotals => {
  const payers = scaledPayers(year);
  const vendors = scaledVendors(year);

  const by_category = vendors.reduce<Record<string, number>>((acc, v) => {
    acc[v.category] = (acc[v.category] ?? 0) + v.total;
    return acc;
  }, {});

  const gross_revenue = sumBy(payers, (p) => p.total);
  const total_expenses = sumBy(vendors, (v) => v.total);

  return {
    gross_revenue,
    total_expenses,
    net_profit: gross_revenue - total_expenses,
    by_category,
    // Deliberately zero: the uncategorized banner prompts an action a
    // pre-active customer has no transactions to take.
    uncategorized_count: 0,
    uncategorized_total: 0,
    transaction_count:
      sumBy(payers, (p) => p.transaction_count) +
      sumBy(vendors, (v) => v.transaction_count),
  };
};

const pctChange = (current: number, previous: number): number =>
  previous === 0
    ? 0
    : Number((((current - previous) / previous) * 100).toFixed(1));

type DemoIncomeStatement = DemoTotals & {
  previous?: DemoTotals;
  delta?: {
    gross_revenue_amount: number;
    gross_revenue_pct: number;
    total_expenses_amount: number;
    total_expenses_pct: number;
    net_profit_amount: number;
    net_profit_pct: number;
  };
};

// `previous` and `delta` are optional here for the same reason they are on the
// API's IncomeStatementResult: they only exist when a comparison was asked for.
export const getDemoIncomeStatement = (
  year: number,
  compareTo?: 'previous'
): DemoIncomeStatement => {
  const totals = totalsForYear(year);
  if (!compareTo) return totals;

  const previous = totalsForYear(year - 1);

  return {
    ...totals,
    previous,
    delta: {
      gross_revenue_amount: totals.gross_revenue - previous.gross_revenue,
      gross_revenue_pct: pctChange(
        totals.gross_revenue,
        previous.gross_revenue
      ),
      total_expenses_amount: totals.total_expenses - previous.total_expenses,
      total_expenses_pct: pctChange(
        totals.total_expenses,
        previous.total_expenses
      ),
      net_profit_amount: totals.net_profit - previous.net_profit,
      net_profit_pct: pctChange(totals.net_profit, previous.net_profit),
    },
  };
};

export const getDemoTopPayers = (year: number, limit = 10) => {
  const payers = scaledPayers(year).sort((a, b) => b.total - a.total);

  return {
    payers: payers.slice(0, limit),
    total_unique_payers: payers.length,
  };
};

export const getDemoTopVendors = (
  year: number,
  category?: string,
  limit = 10
) => {
  const vendors = scaledVendors(year)
    .filter((v) => !category || v.category === category)
    .sort((a, b) => b.total - a.total)
    // `category` is ours for filtering; the API doesn't return it.
    .map(({ vendor, total, transaction_count }) => ({
      vendor,
      total,
      transaction_count,
    }));

  return {
    vendors: vendors.slice(0, limit),
    total_unique_vendors: vendors.length,
  };
};

export const DEMO_REPORT_PACK_ID_PREFIX = 'demo-report-pack-';

// Year-end packs only exist for completed years, and each was generated early
// in the following year — so we offset backwards from today rather than
// hardcoding a date that could land in the future. Snapped to 9am so the
// timestamp is stable across calls: the list and the detail screen generate
// their demo pack independently and must agree on when it was generated.
const DEMO_REPORT_PACK_YEARS_BACK = [1, 2];
const DAYS_AGO_BY_YEARS_BACK: Record<number, number> = { 1: 45, 2: 410 };
const GENERATED_AT_HOUR = 9;

export const getDemoReportPacks = (businessType?: string | null) => {
  const currentYear = dayjs().year();

  return DEMO_REPORT_PACK_YEARS_BACK.map((yearsBack) => {
    const year = currentYear - yearsBack;
    const totals = totalsForYear(year);

    return {
      report_pack_id: `${DEMO_REPORT_PACK_ID_PREFIX}${year}`,
      report_year: year,
      status: REPORT_PACK_STATUS.Ready,
      created_at: dayjs()
        .subtract(DAYS_AGO_BY_YEARS_BACK[yearsBack], 'day')
        .startOf('day')
        .add(GENERATED_AT_HOUR, 'hour')
        .toISOString(),
      business_type: businessType ?? undefined,
      // No disclaimer version: that field records which reviewed legal text a
      // real report was generated under, and a demo pack was generated under
      // none. ReportPackDetail renders the row as "—" and shows the preview
      // notice instead.
      disclaimer_version: undefined,
      summary_json: {
        totals,
        transaction_count: totals.transaction_count,
      },
    };
  });
};

export const getDemoReportPack = (
  reportPackId: string,
  businessType?: string | null
) =>
  getDemoReportPacks(businessType).find(
    (pack) => pack.report_pack_id === reportPackId
  );
